/**
 * Live (D282): watch, and for the person it runs for control, a session running on a workbench, from the
 * console. The address is `/live/:deviceId/:key`, `key` being `c:<conversationId>` or `s:<sessionId>`;
 * `?project=` names the project a watcher came from, so the page finds the row that lets them watch.
 *
 * The header says what the session is, where it runs, how it stands (a StatusMark in the workbench's own words) and
 * who is controlling it, with one control for its person: a menu with Stop remote control (and, for an agent session
 * of its own, End the agent session). Below it the conversation as the workbench draws it (LiveConversation): the tasks
 * it put on the board, the messages and tool calls, and docked at the foot an approval waiting and the composer, whose
 * Stop stops the run. A watcher sees all of it but the composer and the answers. The stream is useLiveSession's long
 * poll over the relay.
 */

import { controllerWords } from '@aioe/shared';
import { Display } from '@gravity-ui/icons';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { connectionMark, LiveConversation, SessionMenu } from '../components/live/LiveParts.tsx';
import { useSessionPerson, workbenchWords } from '../components/live/LiveSessions.tsx';
import { agoWords, clockWords, liveKey, MODE_WORDS, parseLiveKey, statusMark } from '../components/live/liveModel.ts';
import { useLiveSession } from '../components/live/useLiveSession.ts';
import { ArtEmpty, ConfirmDialog, ErrorNote, ListBlock, ListGroup, Page, StatusMark, ViewHeader } from '../components/ui.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { useMoney } from '../money.tsx';

/** A live session is work on a workbench: the workbench view's tone. */
const tone = VIEW_TONES.workbenches!;

export function LivePage() {
  const { deviceId, key } = useParams();
  const [params] = useSearchParams();
  const project = params.get('project') ?? undefined;
  const target = parseLiveKey(key);
  const now = useNow();
  const { format } = useMoney();

  // The row says what the session is, where, and whether this person may control it.
  const mine = useLoad(() => routes.myLive().catch(() => undefined), [], 30_000);
  const watched = useLoad(() => (project ? routes.projectLive(project).catch(() => undefined) : Promise.resolve(undefined)), [project], 30_000);
  const same = (r: { deviceId: string; session: { conversationId?: string; sessionId?: string } }) => r.deviceId === deviceId && liveKey(r.session) === key;
  const row = mine.data?.sessions.find(same) ?? watched.data?.sessions.find(same);
  const settled = !mine.loading && (!project || !watched.loading);
  const canControl = row?.canControl ?? false;
  const projectId = row?.projectId ?? project;
  // Levels 4 and 5 need an owner of the project (D270): known when the project answers, else left to the workbench.
  const role = useLoad(() => (projectId && canControl ? routes.project(projectId).then((p) => p.role).catch(() => undefined) : Promise.resolve(undefined)), [projectId, canControl]);
  const owner = role.data === undefined ? undefined : role.data === 'owner';
  // Pause all / Resume all changes the machine's task board: offered only where the machine allows it (`tasks:write`).
  const scopes = useLoad(() => (deviceId && canControl ? routes.deviceScopes(deviceId).then((r) => r.scopes).catch(() => undefined) : Promise.resolve(undefined)), [deviceId, canControl]);
  // The runtimes the machine last reported: where the usage limit's "Hand off to…" may send the session.
  const runtimes = useLoad(() => (deviceId && canControl ? routes.device(deviceId).then((d) => d.facts?.runtimes).catch(() => undefined) : Promise.resolve(undefined)), [deviceId, canControl]);
  const { account } = useAuth();

  const who = useSessionPerson(row);
  const workbench = workbenchWords(who, row?.deviceName);
  const live = useLiveSession(settled ? deviceId : undefined, target, { workspace: row?.session.rootId, workbench });
  const { state, connection } = live;
  const remote = state.remote;

  const [busy, setBusy] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [confirmEnd, setConfirmEnd] = useState(false);
  const run = async (what: string, fn: () => Promise<string | undefined>) => {
    setBusy(what);
    setProblem(undefined);
    const p = await fn();
    setBusy(undefined);
    setProblem(p);
    return !p;
  };

  const offline = connection === 'offline' || (connection === 'connecting' && row?.online === false);
  const reachable = connection === 'live';
  const remoteOff = remote?.on === false;

  if (!deviceId || !target) {
    return (
      <Page narrow>
        <ArtEmpty tone={tone} icon={<Display />} title="This live session’s address is not complete" action={<Link to="/" className="text-sm text-accent hover:underline">Go to Home</Link>}>
          Open a live session from Home, a project’s History or the Inbox.
        </ArtEmpty>
      </Page>
    );
  }

  const mark = (offline ? connectionMark('offline') : connectionMark(connection)) ?? statusMark(state.status, state.words);
  const by = remote?.controlledBy;
  const controller = by ? `Being controlled from ${controllerWords(by.name, by.via)}${clockWords(by.at, now) ? ` since ${clockWords(by.at, now)}` : ''}` : remote?.on ? 'Open to the web and the phone' : remoteOff ? 'Remote control is off for this session' : undefined;
  const kind = row ? MODE_WORDS[row.session.mode] : 'Live session';
  const lastSeen = agoWords(row?.lastSeenAt, now);
  const usage = state.usage;
  const usageLine = usage
    ? [
        usage.contextUsed !== undefined ? `Context ${usage.contextUsed.toLocaleString()}${usage.contextSize ? ` of ${usage.contextSize.toLocaleString()} (${Math.round((usage.contextUsed / usage.contextSize) * 100)}%)` : ''}` : undefined,
        usage.costUsd !== undefined ? `${format(usage.costUsd)} so far` : undefined,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <Page narrow>
      <ViewHeader
        tone={tone}
        icon={<Display />}
        title={row?.session.title || 'Live session'}
        blurb={
          <>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <StatusMark state={mark.state} label={mark.label} />
              <span>
                {kind} on {workbench}
              </span>
            </span>
            {controller ? <span className="block">{controller}</span> : null}
          </>
        }
        actions={
          canControl ? (
            <SessionMenu
              remoteOn={remote?.on === true}
              reachable={reachable && !busy}
              agentSession={Boolean(target.sessionId)}
              onStopRemote={() => void run('remote', live.endRemote)}
              onEnd={() => setConfirmEnd(true)}
            />
          ) : undefined
        }
      />
      <PartBoundary key="live" name="Live session" tone={tone}>
        {offline ? (
          <ArtEmpty tone={tone} icon={<Display />} title={`${workbench} is not reachable.`}>
            {lastSeen ? `It was last seen ${lastSeen}. ` : ''}This page tries again every 15 seconds and carries on when it answers.
          </ArtEmpty>
        ) : null}
        <ErrorNote error={connection === 'refused' ? live.problem : undefined} />
        <ErrorNote error={problem} />
        <ListGroup title="Conversation" description={usageLine ? `${usageLine}.` : undefined}>
          <ListBlock flush>
          <LiveConversation live={live} canControl={canControl} who={who} me={account?.id} owner={owner} mode={row?.session.mode} projectId={projectId} canPause={scopes.data?.includes('tasks:write') ?? false} runtimes={runtimes.data} />
          </ListBlock>
        </ListGroup>
      </PartBoundary>
      <ConfirmDialog
        isOpen={confirmEnd}
        title="End this agent session?"
        confirmLabel="End the session"
        danger
        onCancel={() => setConfirmEnd(false)}
        onConfirm={async () => {
          await run('stop', live.stop);
          setConfirmEnd(false);
        }}
      >
        <p className="m-0">The agent stops on {workbench}. What it has done so far stays. To stop only what it is doing now, use Stop beside the message box.</p>
      </ConfirmDialog>
    </Page>
  );
}

